import { parseTextLine } from './TextFormatter';
import type { LineFormatType } from './TextFormatter';
import {
  getCurrentLine,
  getLineContent,
  createLineElement,
  updateLineFormat,
  setCursorAtLineStart,
  detectFormatTrigger,
  getNextListNumber,
  renumberListItems,
  isCursorAtLineStart,
  LINE_CLASS,
} from './WysiwygEditor';

export function handleTabIndent(input: HTMLTextAreaElement, shift: boolean): boolean {
  const value = input.value;
  const selectionStart = input.selectionStart ?? 0;
  const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
  const lineEndIndex = value.indexOf('\n', selectionStart);
  const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex;
  const currentLine = value.slice(lineStart, lineEnd);

  if (shift) {
    // Shift+Tab: Remove up to 2 spaces from line start
    const dedentMatch = currentLine.match(/^(\s{1,2})/);
    if (!dedentMatch) return false;

    const spacesToRemove = dedentMatch[1]?.length ?? 0;
    const before = value.slice(0, lineStart);
    const after = value.slice(lineStart + spacesToRemove);
    input.value = `${before}${after}`;
    input.selectionStart = Math.max(lineStart, selectionStart - spacesToRemove);
    input.selectionEnd = input.selectionStart;
  } else {
    // Tab: Insert 2 spaces at line start
    const before = value.slice(0, lineStart);
    const after = value.slice(lineStart);
    input.value = `${before}  ${after}`;
    input.selectionStart = selectionStart + 2;
    input.selectionEnd = input.selectionStart;
  }

  input.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}

export function handleSmartEnter(input: HTMLTextAreaElement): boolean {
  const value = input.value;
  const selectionStart = input.selectionStart ?? value.length;
  const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
  const lineEndIndex = value.indexOf('\n', selectionStart);
  const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex;
  const currentLine = value.slice(lineStart, lineEnd);

  const format = parseTextLine(currentLine);

  // If it's a list and the content is empty, exit the list
  if ((format.type === 'ordered' || format.type === 'unordered') && !format.content.trim()) {
    // Remove the list prefix and insert plain newline
    const before = value.slice(0, lineStart);
    const after = value.slice(lineEnd);
    input.value = `${before}\n${after}`;
    const newCursor = lineStart + 1;
    input.selectionStart = newCursor;
    input.selectionEnd = newCursor;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  // If it's a list with content, continue the list
  if (format.type === 'ordered') {
    const nextNumber = (format.listNumber ?? 1) + 1;
    const indent = '  '.repeat(format.indent);
    const prefix = `${indent}${nextNumber}. `;
    const before = value.slice(0, selectionStart);
    const after = value.slice(selectionStart);
    const insertion = `\n${prefix}`;
    input.value = `${before}${insertion}${after}`;
    const newCursor = before.length + insertion.length;
    input.selectionStart = newCursor;
    input.selectionEnd = newCursor;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  if (format.type === 'unordered') {
    const indent = '  '.repeat(format.indent);
    const prefix = `${indent}- `;
    const before = value.slice(0, selectionStart);
    const after = value.slice(selectionStart);
    const insertion = `\n${prefix}`;
    input.value = `${before}${insertion}${after}`;
    const newCursor = before.length + insertion.length;
    input.selectionStart = newCursor;
    input.selectionEnd = newCursor;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  // Plain newline for normal text
  const before = value.slice(0, selectionStart);
  const after = value.slice(selectionStart);
  input.value = `${before}\n${after}`;
  const newCursor = before.length + 1;
  input.selectionStart = newCursor;
  input.selectionEnd = newCursor;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}

export function handleAutoFormat(input: HTMLTextAreaElement): boolean {
  const value = input.value;
  const selectionStart = input.selectionStart ?? value.length;
  const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
  const textBeforeCursor = value.slice(lineStart, selectionStart);

  // Check for auto-format patterns (lists only)
  // Note: Header patterns (# ## ###) are NOT auto-formatted.
  // Headers are applied via toolbar only.
  const patterns = [
    { regex: /^(\s*)(\d+)\.\s$/, type: 'ordered' as const },
    { regex: /^(\s*)([-*])\s$/, type: 'unordered' as const },
  ];

  for (const pattern of patterns) {
    const match = textBeforeCursor.match(pattern.regex);
    if (match) {
      // Pattern matched - the formatting is already in place, no action needed
      // This prevents double-formatting
      return true;
    }
  }

  return false;
}

export const continueListOnShiftEnter = (input: HTMLTextAreaElement): boolean => {
  const value = input.value;
  const selectionStart = input.selectionStart ?? value.length;
  const selectionEnd = input.selectionEnd ?? value.length;
  const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
  const lineEndIndex = value.indexOf('\n', selectionEnd);
  const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex;
  const currentLine = value.slice(lineStart, lineEnd);

  const orderedMatch = currentLine.match(/^(\s*)(\d+)\.\s+/);
  const unorderedMatch = orderedMatch ? null : currentLine.match(/^(\s*)([-*])\s+/);
  let prefix: string | null = null;

  if (orderedMatch) {
    const indent = orderedMatch[1] ?? '';
    const numberText = orderedMatch[2];
    if (numberText) {
      const currentNumber = Number.parseInt(numberText, 10);
      if (!Number.isNaN(currentNumber)) {
        prefix = `${indent}${currentNumber + 1}. `;
      }
    }
  } else if (unorderedMatch) {
    const indent = unorderedMatch[1] ?? '';
    const marker = unorderedMatch[2] ?? '-';
    prefix = `${indent}${marker} `;
  }

  if (!prefix) return false;

  const before = value.slice(0, selectionStart);
  const after = value.slice(selectionEnd);
  const insertion = `\n${prefix}`;
  input.value = `${before}${insertion}${after}`;
  const newCursor = before.length + insertion.length;
  input.selectionStart = newCursor;
  input.selectionEnd = newCursor;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
};

export const handleToolbarBlur = (
  event: FocusEvent,
  finishEditing: () => void,
  requestRefocus: () => void
): void => {
  const focusTarget = (event.relatedTarget ?? document.activeElement) as HTMLElement | null;
  const toolbarTarget = focusTarget?.closest('.text-format-row, .toolbar');
  if (!toolbarTarget) {
    finishEditing();
    return;
  }

  const isDelayedRefocusTarget =
    focusTarget instanceof HTMLSelectElement ||
    (focusTarget instanceof HTMLInputElement && focusTarget.type === 'color');

  if (isDelayedRefocusTarget) {
    const refocus = (): void => {
      setTimeout(() => requestRefocus(), 0);
    };
    focusTarget.addEventListener('change', refocus, { once: true });
    focusTarget.addEventListener('blur', refocus, { once: true });
    return;
  }

  setTimeout(() => requestRefocus(), 0);
};

// ============================================================================
// WYSIWYG Contenteditable Handlers
// ============================================================================

export function handleWysiwygEnter(editor: HTMLDivElement): boolean {
  const selection = window.getSelection();
  const currentLine = getCurrentLine(selection);

  if (!currentLine) {
    // No line context, create a new normal line
    const newLine = createLineElement('normal', 0);
    newLine.appendChild(document.createElement('br'));
    editor.appendChild(newLine);
    setCursorAtLineStart(newLine);
    return true;
  }

  const type = currentLine.getAttribute('data-type') as LineFormatType;
  const indent = Number.parseInt(currentLine.getAttribute('data-indent') ?? '0', 10);
  const content = getLineContent(currentLine);

  // If list item is empty, exit the list (convert to normal)
  if ((type === 'ordered' || type === 'unordered') && !content.trim()) {
    updateLineFormat(currentLine, 'normal');
    setCursorAtLineStart(currentLine);
    editor.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  // Create new line with appropriate format
  let newLine: HTMLDivElement;

  if (type === 'ordered') {
    const nextNum = getNextListNumber(currentLine);
    newLine = createLineElement('ordered', indent, nextNum);
    newLine.appendChild(document.createElement('br'));
    currentLine.after(newLine);
    // Renumber subsequent items
    if (newLine.nextElementSibling) {
      renumberListItems(newLine.nextElementSibling as HTMLElement, nextNum + 1);
    }
  } else if (type === 'unordered') {
    newLine = createLineElement('unordered', indent);
    newLine.appendChild(document.createElement('br'));
    currentLine.after(newLine);
  } else {
    // Normal text - just create new normal line
    newLine = createLineElement('normal', 0);
    newLine.appendChild(document.createElement('br'));
    currentLine.after(newLine);
  }

  setCursorAtLineStart(newLine);
  editor.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}

export function handleWysiwygTab(editor: HTMLDivElement, isShift: boolean): boolean {
  const selection = window.getSelection();
  const currentLine = getCurrentLine(selection);

  if (!currentLine) return false;

  const currentIndent = Number.parseInt(currentLine.getAttribute('data-indent') ?? '0', 10);

  if (isShift) {
    // Shift+Tab: decrease indent
    if (currentIndent <= 0) return false;
    const newIndent = currentIndent - 1;
    currentLine.setAttribute('data-indent', String(newIndent));
    currentLine.style.marginLeft = newIndent > 0 ? `${newIndent * 1.5}em` : '';
  } else {
    // Tab: increase indent
    const newIndent = currentIndent + 1;
    currentLine.setAttribute('data-indent', String(newIndent));
    currentLine.style.marginLeft = `${newIndent * 1.5}em`;
  }

  editor.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}

export function handleWysiwygAutoFormat(editor: HTMLDivElement): boolean {
  const selection = window.getSelection();
  const currentLine = getCurrentLine(selection);

  if (!currentLine) return false;

  const type = currentLine.getAttribute('data-type') as LineFormatType;

  // Only auto-format normal lines (avoid re-formatting already formatted lines)
  if (type !== 'normal') return false;

  const content = getLineContent(currentLine);
  const trigger = detectFormatTrigger(content);

  if (!trigger) return false;

  // Transform the line
  updateLineFormat(currentLine, trigger.type, trigger.listNumber);

  // Remove the trigger text from content (e.g., "# " becomes header without "#")
  // Clear the line content and set cursor
  const marker = currentLine.querySelector('.wysiwyg-marker');
  const childNodes = Array.from(currentLine.childNodes);
  for (const child of childNodes) {
    if (child !== marker) {
      currentLine.removeChild(child);
    }
  }

  // Add empty content placeholder
  currentLine.appendChild(document.createElement('br'));
  setCursorAtLineStart(currentLine);

  editor.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}

export function handleWysiwygBackspace(editor: HTMLDivElement): boolean {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);
  if (!range.collapsed) return false; // Let browser handle selection deletion

  const currentLine = getCurrentLine(selection);
  if (!currentLine) return false;

  const type = currentLine.getAttribute('data-type') as LineFormatType;

  // If at start of a formatted line, remove formatting first
  if (isCursorAtLineStart(selection, currentLine) && type !== 'normal') {
    updateLineFormat(currentLine, 'normal');
    setCursorAtLineStart(currentLine);
    editor.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  // If at start of a normal line and there's a previous line, merge them
  if (isCursorAtLineStart(selection, currentLine) && type === 'normal') {
    const prevLine = currentLine.previousElementSibling as HTMLElement | null;
    if (prevLine && prevLine.classList.contains(LINE_CLASS)) {
      const content = getLineContent(currentLine);

      // Remove BR from previous line if present
      const prevBr = prevLine.querySelector('br');
      prevBr?.remove();

      // Append content to previous line
      if (content) {
        prevLine.appendChild(document.createTextNode(content));
      }

      // Remove current line
      currentLine.remove();

      // Set cursor at end of merged content
      const lastChild = prevLine.lastChild;
      if (lastChild && lastChild.nodeType === Node.TEXT_NODE) {
        const textLength = (lastChild as Text).length - content.length;
        range.setStart(lastChild, Math.max(0, textLength));
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      editor.dispatchEvent(new Event('input', { bubbles: true }));
      return true;
    }
  }

  // Let browser handle normal backspace
  return false;
}

export function handleWysiwygPaste(editor: HTMLDivElement, event: ClipboardEvent): boolean {
  event.preventDefault();

  const text = event.clipboardData?.getData('text/plain') ?? '';
  if (!text) return true;

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return true;

  // Delete any selected content first
  const range = selection.getRangeAt(0);
  if (!range.collapsed) {
    range.deleteContents();
  }

  // Insert text as plain text, preserving newlines
  const lines = text.split('\n');

  if (lines.length === 1) {
    // Single line - just insert at cursor
    const textNode = document.createTextNode(lines[0] ?? '');
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    // Multi-line - need to handle line structure
    const currentLine = getCurrentLine(selection);

    if (currentLine) {
      // Get content after cursor in current line
      const afterCursor = range.cloneRange();
      afterCursor.selectNodeContents(currentLine);
      afterCursor.setStart(range.startContainer, range.startOffset);
      const afterContent = afterCursor.extractContents();

      // Insert first line's text at cursor
      const firstLineText = lines[0];
      if (firstLineText) {
        range.insertNode(document.createTextNode(firstLineText));
      }

      // Create new lines for remaining text
      let lastInsertedLine = currentLine;
      for (let i = 1; i < lines.length; i++) {
        const newLine = createLineElement('normal', 0);
        const lineText = lines[i];
        if (lineText) {
          newLine.appendChild(document.createTextNode(lineText));
        } else {
          newLine.appendChild(document.createElement('br'));
        }
        lastInsertedLine.after(newLine);
        lastInsertedLine = newLine;
      }

      // Append after-cursor content to last line
      if (afterContent.textContent) {
        lastInsertedLine.appendChild(afterContent);
      }

      // Remove BR if line has content
      const br = lastInsertedLine.querySelector('br');
      if (br && lastInsertedLine.textContent) {
        br.remove();
      }

      // Position cursor at end of last inserted line
      const newRange = document.createRange();
      newRange.selectNodeContents(lastInsertedLine);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
  }

  editor.dispatchEvent(new Event('input', { bubbles: true }));
  return true;
}
